import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { getValidToken, API_URL } from './utils/authUtils';
import { devLogSafe, devLog, devError } from './utils/secureLogger';
import { nacmeLogo } from './assets/images';
import { ResponsiveTable, MobileAdminStudentMatchCard, MobileSidebar } from './components/ResponsiveComponents';

const BACKEND_URL = API_URL;

// Component for collapsible requirement dropdowns in organization cards
const OrgRequirementDropdown = ({ label, items, orgIndex, fieldName, expandedItems, setExpandedItems }) => {
  const dropdownKey = `org-${orgIndex}-${fieldName}`;
  const isExpanded = expandedItems.has(dropdownKey);
  
  const toggleDropdown = (e) => {
    e.stopPropagation(); // Prevent triggering other expansions
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(dropdownKey)) {
      newExpanded.delete(dropdownKey);
    } else {
      newExpanded.add(dropdownKey);
    }
    setExpandedItems(newExpanded);
  };

  if (!items || items.length === 0) {
    return (
      <div>
        <p className="font-medium text-gray-700 dark:text-gray-300 text-xs">{label}:</p>
        <p className="text-gray-600 dark:text-gray-400 text-xs">Not specified</p>
      </div>
    );
  }

  const displayText = items.length === 1 ? items[0] : `${items.length} items`;
  const shouldShowDropdown = items.length > 1;

  return (
    <div>
      <p className="font-medium text-gray-700 dark:text-gray-300 text-xs">{label}:</p>
      {shouldShowDropdown ? (
        <div>
          <button
            onClick={toggleDropdown}
            className="flex items-center space-x-1 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors text-xs"
          >
            <span>{displayText}</span>
            <svg 
              className={`w-3 h-3 transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {isExpanded && (
            <div className="mt-1 p-2 bg-gray-50 dark:bg-gray-600 rounded border text-xs">
              <ul className="list-disc list-inside space-y-1">
                {items.map((item, idx) => (
                  <li key={idx} className="text-gray-700 dark:text-gray-300 text-xs">{item}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      ) : (
        <p className="text-gray-600 dark:text-gray-400 text-xs">{displayText}</p>
      )}
    </div>
  );
};

const AdminStudentMatches = () => {
  const navigate = useNavigate();
  const [studentMatches, setStudentMatches] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedStudents, setExpandedStudents] = useState(new Set());
  const [expandedOrgItems, setExpandedOrgItems] = useState(new Set());
  const [expandedMobileCards, setExpandedMobileCards] = useState(new Set());
  const [debugMode, setDebugMode] = useState(false);
  const [debugInfo, setDebugInfo] = useState(null);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  
  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [universityFilter, setUniversityFilter] = useState('');
  const [selectedStudents, setSelectedStudents] = useState(new Set());
  const [showBulkTokens, setShowBulkTokens] = useState(false);
  
  // Token generation state
  const [tokenGenerating, setTokenGenerating] = useState(false);
  const [tokenSuccess, setTokenSuccess] = useState(null);
  const [tokenExpiryHours, setTokenExpiryHours] = useState(720);

  useEffect(() => {
    fetchStudentMatches();
  }, []);

  // Filter students based on search term and university
  useEffect(() => {
    let filtered = studentMatches;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(studentData => {
        const student = studentData.student_info;
        const searchLower = searchTerm.toLowerCase();
        return (
          student.name.toLowerCase().includes(searchLower) ||
          student.email.toLowerCase().includes(searchLower) ||
          student.university.toLowerCase().includes(searchLower)
        );
      });
    }

    // Apply university filter
    if (universityFilter) {
      filtered = filtered.filter(studentData => 
        studentData.student_info.university.toLowerCase().includes(universityFilter.toLowerCase())
      );
    }

    setFilteredStudents(filtered);
  }, [studentMatches, searchTerm, universityFilter]);

  const fetchStudentMatches = async (withDebug = false) => {
    try {
      const token = getValidToken();
      if (!token) {
        setError('Your session has expired. Please log in again.');
        setLoading(false);
        setTimeout(() => {
          navigate('/');
        }, 2000);
        return;
      }

      const url = withDebug 
        ? `${BACKEND_URL}/api/student-matches?debug=true`
        : `${BACKEND_URL}/api/student-matches`;

      const response = await axios.get(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const students = response.data.students || [];
      // Sort alphabetically by name
      students.sort((a, b) => a.student_info.name.toLowerCase().localeCompare(b.student_info.name.toLowerCase()));
      
      setStudentMatches(students);
      setFilteredStudents(students);
      setDebugInfo(response.data.debug || null);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching student matches:', err);
      if (err.response?.status === 401) {
        setError('Your session has expired. Please log in again.');
        localStorage.removeItem('token');
        setTimeout(() => {
          navigate('/');
        }, 2000);
      } else {
        setError(err.response?.data?.detail || 'Failed to fetch student matches. Please ensure you are logged in as an admin.');
      }
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/admin/login');
  };

  const toggleStudent = (index) => {
    const newExpanded = new Set(expandedStudents);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedStudents(newExpanded);
  };

  const toggleMobileCard = (index) => {
    const newExpanded = new Set(expandedMobileCards);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedMobileCards(newExpanded);
  };

  const expandAll = () => {
    setExpandedStudents(new Set(studentMatches.map((_, index) => index)));
  };

  const collapseAll = () => {
    setExpandedStudents(new Set());
  };

  // Get unique universities for filter dropdown
  const getUniqueUniversities = () => {
    const universities = studentMatches.map(student => student.student_info.university);
    return [...new Set(universities)].sort();
  };

  // Toggle student selection
  const toggleStudentSelection = (studentId) => {
    const newSelected = new Set(selectedStudents);
    if (newSelected.has(studentId)) {
      newSelected.delete(studentId);
    } else {
      newSelected.add(studentId);
    }
    setSelectedStudents(newSelected);
  };

  // Select all filtered students
  const selectAllFiltered = () => {
    const allFilteredIds = filteredStudents.map(student => student.student_info.id);
    setSelectedStudents(new Set(allFilteredIds));
  };

  // Clear all selections
  const clearSelections = () => {
    setSelectedStudents(new Set());
  };

  // Generate update token for individual student
  const generateIndividualToken = async (studentId) => {
    try {
      setTokenGenerating(true);
      const token = getValidToken();
      
      const response = await axios.post(`${BACKEND_URL}/api/generate-update-tokens`, {
        applicant_ids: [studentId],
        expires_in_hours: tokenExpiryHours
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      setTokenSuccess(`Update token generated successfully for student ID ${studentId}`);
      setTimeout(() => setTokenSuccess(null), 5000);
      
    } catch (err) {
      console.error('Error generating token:', err);
      setError(`Failed to generate update token: ${err.response?.data?.detail || 'Unknown error'}`);
      setTimeout(() => setError(null), 5000);
    } finally {
      setTokenGenerating(false);
    }
  };

  // Generate bulk update tokens
  const generateBulkTokens = async () => {
    if (selectedStudents.size === 0) {
      alert('Please select at least one student to generate tokens.');
      return;
    }

    try {
      setTokenGenerating(true);
      const token = getValidToken();
      
      const response = await axios.post(`${BACKEND_URL}/api/generate-update-tokens`, {
        applicant_ids: Array.from(selectedStudents),
        expires_in_hours: tokenExpiryHours
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      setTokenSuccess(`Update tokens generated successfully for ${selectedStudents.size} students`);
      setSelectedStudents(new Set()); // Clear selections
      setShowBulkTokens(false); // Hide bulk panel
      setTimeout(() => setTokenSuccess(null), 5000);
      
    } catch (err) {
      console.error('Error generating bulk tokens:', err);
      setError(`Failed to generate bulk tokens: ${err.response?.data?.detail || 'Unknown error'}`);
      setTimeout(() => setError(null), 5000);
    } finally {
      setTokenGenerating(false);
    }
  };

  // Basic CSV export without token data
  const exportBasicCSV = () => {
    if (filteredStudents.length === 0) {
      alert('No student matches to export.');
      return;
    }

    // CSV headers
    const headers = [
      'Student Name',
      'Email',
      'University',
      'GPA',
      'Major',
      'Class Year',
      'Total Matches',
      'Matching Organizations'
    ];

    // Convert matches to CSV rows
    const csvRows = [
      headers.join(','), // Header row
      ...filteredStudents.map(studentData => [
        `"${studentData.student_info.name}"`,
        studentData.student_info.email,
        `"${studentData.student_info.university}"`,
        studentData.student_info.gpa,
        `"${studentData.student_info.major}"`,
        `"${studentData.student_info.class_year}"`,
        studentData.match_count,
        `"${studentData.matching_organizations.map(org => org.organization).join('; ')}"`,
      ].join(','))
    ];

    // Create CSV content
    const csvContent = csvRows.join('\n');

    // Create and download the file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      
      // Generate filename with timestamp
      const timestamp = new Date().toISOString().slice(0, 10);
      link.setAttribute('download', `Student_Organization_Matches_${timestamp}.csv`);
      
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // CSV Export functionality with token data
  const exportToCSV = async () => {
    if (filteredStudents.length === 0) {
      alert('No student matches to export.');
      return;
    }

    try {
      // Show loading state
      setTokenGenerating(true);
      
      // Fetch token data for all students (handle pagination if needed)
      const token = getValidToken();
      let allTokens = [];
      let offset = 0;
      const limit = 1000;
      let hasMore = true;
      
      // Fetch tokens in batches if there are more than 1000
      while (hasMore) {
        const tokenResponse = await axios.get(
          `${BACKEND_URL}/api/list-update-tokens?active_only=false&limit=${limit}&offset=${offset}`, 
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );
        
        const batchTokens = tokenResponse.data.tokens || [];
        allTokens = [...allTokens, ...batchTokens];
        
        // Check if we've fetched all tokens
        if (batchTokens.length < limit || allTokens.length >= tokenResponse.data.total) {
          hasMore = false;
        } else {
          offset += limit;
        }
      }

      // Create a map of applicant_id to token data
      const tokenMap = {};
      
      devLog(`Found ${allTokens.length} tokens from API`);
      
      allTokens.forEach(tokenData => {
        // Each token has a single applicant_id
        const applicantId = tokenData.applicant_id;
        
        // Keep the most recent active token for each applicant
        const isActive = !tokenData.is_expired && !tokenData.used;
        
        // Only map if we don't have a token for this applicant yet, or if this is an active token
        if (!tokenMap[applicantId] || (isActive && (tokenMap[applicantId].is_expired || tokenMap[applicantId].used))) {
          tokenMap[applicantId] = {
            ...tokenData,
            status: isActive ? 'active' : 'inactive'
          };
        }
      });
      
      devLog(`Mapped tokens for ${Object.keys(tokenMap).length} applicants`);

      // Calculate hours remaining for each token
      const calculateHoursRemaining = (expiresAt) => {
        const now = new Date();
        const expiry = new Date(expiresAt);
        const hoursRemaining = (expiry - now) / (1000 * 60 * 60); // Convert ms to hours
        return hoursRemaining > 0 ? Math.round(hoursRemaining) : 0;
      };

      // CSV headers
      const headers = [
        'Student Name',
        'Email',
        'University',
        'GPA',
        'Major',
        'Class Year',
        'Total Matches',
        'Matching Organizations',
        'Update Token',
        'Token Hours Remaining'
      ];

      // Debug: Show some student IDs
      if (filteredStudents.length > 0) {
        devLog('Sample student IDs:', filteredStudents.slice(0, 3).map(s => s.student_info.id));
        devLog('Sample token map keys:', Object.keys(tokenMap).slice(0, 10));
      }

      // Convert matches to CSV rows
      const csvRows = [
        headers.join(','), // Header row
        ...filteredStudents.map(studentData => {
          const studentId = studentData.student_info.id;
          const tokenData = tokenMap[studentId];
          const hasActiveToken = tokenData && tokenData.status === 'active';
          
          if (studentId && tokenData) {
            devLogSafe(`Found token for student ${studentId}`, { token: tokenData.token.substring(0, 10) + '...' });
          }
          
          return [
            `"${studentData.student_info.name}"`,
            studentData.student_info.email,
            `"${studentData.student_info.university}"`,
            studentData.student_info.gpa,
            `"${studentData.student_info.major}"`,
            `"${studentData.student_info.class_year}"`,
            studentData.match_count,
            `"${studentData.matching_organizations.map(org => org.organization).join('; ')}"`,
            hasActiveToken ? tokenData.token : '',
            hasActiveToken ? calculateHoursRemaining(tokenData.expires_at) : ''
          ].join(',');
        })
      ];

      // Create CSV content
      const csvContent = csvRows.join('\n');

      // Create and download the file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        
        // Generate filename with timestamp
        const timestamp = new Date().toISOString().slice(0, 10);
        link.setAttribute('download', `Student_Organization_Matches_with_Tokens_${timestamp}.csv`);
        
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (err) {
      console.error('Error exporting CSV with token data:', err);
      console.error('Error details:', err.response?.data);
      
      // If token fetch failed, export without token data
      if (err.response?.status === 404 || err.response?.status === 500) {
        const confirmExport = window.confirm('Unable to fetch token data. Export CSV without token information?');
        if (confirmExport) {
          exportBasicCSV();
        }
      } else {
        alert(`Failed to export CSV: ${err.response?.data?.detail || err.message || 'Unknown error'}`);
      }
    } finally {
      setTokenGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white dark:from-gray-900 dark:to-gray-800 p-8">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-8"></div>
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-32 bg-gray-200 dark:bg-gray-700 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white dark:from-gray-900 dark:to-gray-800 p-8">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-100 text-red-700 p-4 rounded-md">
            {error}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white dark:from-gray-900 dark:to-gray-800">
      {/* Mobile Sidebar */}
      <MobileSidebar
        activeTab="student-matches"
        onTabChange={(tabId) => {
          if (tabId === 'home') {
            navigate('/admin');
          } else if (tabId === 'students') {
            // Students button handled by grid menu
          } else if (tabId === 'recruiters') {
            navigate('/admin?tab=recruiters');
          } else if (tabId === 'profile') {
            // Logout functionality
            localStorage.removeItem('token');
            navigate('/admin');
          }
        }}
        navigate={navigate}
        isOpen={isMobileSidebarOpen}
        onToggle={() => setIsMobileSidebarOpen(!isMobileSidebarOpen)}
      />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <div className="flex flex-col gap-4">
            {/* Logo and Title Row */}
            <div className="flex items-center space-x-3 sm:space-x-4">
              <img
                src={nacmeLogo}
                alt="NACME Logo"
                className="h-10 sm:h-12 w-auto drop-shadow"
              />
              <h1 className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white">
                Student Organization Matches
              </h1>
            </div>
            
            {/* Action Buttons Row */}
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 sm:justify-end">
              <button
                onClick={() => navigate('/admin')}
                className="w-full sm:w-auto px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-center text-sm sm:text-base"
              >
                <span className="hidden sm:inline">Back to Admin Panel</span>
                <span className="sm:hidden">Back to Admin</span>
              </button>
              <button
                onClick={handleLogout}
                className="w-full sm:w-auto px-3 sm:px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm sm:text-base"
              >
                Logout
              </button>
            </div>
          </div>
        </div>

        {/* Success/Error Messages */}
        {tokenSuccess && (
          <div className="mb-6 bg-green-100 text-green-700 p-4 rounded-md">
            {tokenSuccess}
          </div>
        )}
        {error && (
          <div className="mb-6 bg-red-100 text-red-700 p-4 rounded-md">
            {error}
          </div>
        )}

        {/* Summary Statistics */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4 sm:p-6 mb-6">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-xl sm:text-2xl font-bold text-blue-600 dark:text-blue-400">
                {filteredStudents.length}
              </div>
              <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                {searchTerm || universityFilter ? 'Filtered' : 'Total'} Students
              </div>
            </div>
            <div className="text-center">
              <div className="text-xl sm:text-2xl font-bold text-purple-600 dark:text-purple-400">
                {studentMatches.length}
              </div>
              <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                Students with Matches
              </div>
            </div>
            <div className="text-center">
              <div className="text-xl sm:text-2xl font-bold text-green-600 dark:text-green-400">
                {studentMatches.reduce((sum, student) => sum + student.match_count, 0)}
              </div>
              <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                Total Match Connections
              </div>
            </div>
            <div className="text-center">
              <div className="text-xl sm:text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                {studentMatches.length > 0 ? Math.round(studentMatches.reduce((sum, student) => sum + student.match_count, 0) / studentMatches.length * 10) / 10 : 0}
              </div>
              <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                Average Matches per Student
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filter Section */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4 sm:p-6 mb-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            {/* Search Bar */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Search Students
              </label>
              <input
                type="text"
                placeholder="Search by name, email, or university..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white text-sm sm:text-base"
              />
            </div>

            {/* University Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Filter by University
              </label>
              <select
                value={universityFilter}
                onChange={(e) => setUniversityFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white text-sm sm:text-base"
              >
                <option value="">All Universities</option>
                {getUniqueUniversities().map(university => (
                  <option key={university} value={university}>
                    {university}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Selection Controls */}
          {filteredStudents.length > 0 && (
            <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2 items-center">
              <button
                onClick={selectAllFiltered}
                className="px-3 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
              >
                <span className="hidden sm:inline">Select All ({filteredStudents.length})</span>
                <span className="sm:hidden">Select All</span>
              </button>
              <button
                onClick={clearSelections}
                className="px-3 py-2 bg-gray-600 text-white text-sm rounded hover:bg-gray-700 transition-colors"
              >
                <span className="hidden sm:inline">Clear Selection</span>
                <span className="sm:hidden">Clear</span>
              </button>
              {selectedStudents.size > 0 && (
                <>
                  <span className="col-span-2 sm:col-span-1 text-sm text-gray-600 dark:text-gray-400 text-center sm:text-left">
                    {selectedStudents.size} selected
                  </span>
                  <button
                    onClick={() => setShowBulkTokens(!showBulkTokens)}
                    className="col-span-2 sm:col-span-1 px-3 py-2 bg-green-600 text-white text-sm rounded hover:bg-green-700 transition-colors"
                  >
                    <span className="hidden sm:inline">Generate Tokens ({selectedStudents.size})</span>
                    <span className="sm:hidden">Generate ({selectedStudents.size})</span>
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        {/* Bulk Token Generation Panel */}
        {showBulkTokens && selectedStudents.size > 0 && (
          <div className="bg-yellow-50 dark:bg-yellow-900 border border-yellow-200 dark:border-yellow-700 rounded-lg p-4 sm:p-6 mb-6">
            <h3 className="text-base sm:text-lg font-semibold text-yellow-800 dark:text-yellow-200 mb-3">
              Generate Update Tokens
            </h3>
            <p className="text-sm sm:text-base text-yellow-700 dark:text-yellow-300 mb-4">
              You are about to generate update tokens for {selectedStudents.size} selected students.
            </p>

            {/* Token Expiry Input */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-yellow-800 dark:text-yellow-200 mb-2">
                Token Expiration (hours)
              </label>
              <input
                type="number"
                value={tokenExpiryHours}
                onChange={(e) => setTokenExpiryHours(parseInt(e.target.value) || 720)}
                min="1"
                max="8760"
                className="w-full sm:w-48 px-3 py-2 border border-yellow-300 dark:border-yellow-600 rounded-md shadow-sm focus:outline-none focus:ring-yellow-500 focus:border-yellow-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              />
              <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-1">
                Default: 720 hours (30 days). Max: 8760 hours (365 days)
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
              <button
                onClick={generateBulkTokens}
                disabled={tokenGenerating}
                className="w-full sm:w-auto px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm sm:text-base"
              >
                {tokenGenerating ? (
                  <>
                    <svg className="animate-spin w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 2v10l4-4" />
                    </svg>
                    <span className="hidden sm:inline">Generating...</span>
                    <span className="sm:hidden">Generating...</span>
                  </>
                ) : (
                  <>
                    <span className="hidden sm:inline">Confirm Generate Tokens</span>
                    <span className="sm:hidden">Generate Tokens</span>
                  </>
                )}
              </button>
              <button
                onClick={() => setShowBulkTokens(false)}
                className="w-full sm:w-auto px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm sm:text-base"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Controls */}
        <div className="mb-6 grid grid-cols-2 sm:flex sm:flex-wrap gap-2 sm:gap-4">
          <button
            onClick={expandAll}
            className="px-3 sm:px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm sm:text-base"
          >
            <span className="hidden sm:inline">Expand All</span>
            <span className="sm:hidden">Expand</span>
          </button>
          <button
            onClick={collapseAll}
            className="px-3 sm:px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm sm:text-base"
          >
            <span className="hidden sm:inline">Collapse All</span>
            <span className="sm:hidden">Collapse</span>
          </button>
          <button
            onClick={() => fetchStudentMatches()}
            className="px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm sm:text-base"
          >
            <span className="hidden sm:inline">Refresh Data</span>
            <span className="sm:hidden">Refresh</span>
          </button>
          <button
            onClick={exportToCSV}
            disabled={tokenGenerating}
            className="col-span-2 sm:col-span-1 px-3 sm:px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm sm:text-base"
          >
            {tokenGenerating ? (
              <>
                <svg className="animate-spin w-3 sm:w-4 h-3 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span className="hidden sm:inline">Exporting...</span>
                <span className="sm:hidden">Exporting...</span>
              </>
            ) : (
              <>
                <svg className="w-3 sm:w-4 h-3 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span className="hidden sm:inline">Export CSV with Tokens</span>
                <span className="sm:hidden">Export CSV</span>
              </>
            )}
          </button>
          <button
            onClick={exportBasicCSV}
            className="px-3 sm:px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors flex items-center justify-center gap-2 text-sm sm:text-base"
            title="Export without token data"
          >
            <svg className="w-3 sm:w-4 h-3 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <span className="hidden sm:inline">Basic Export</span>
            <span className="sm:hidden">Basic</span>
          </button>
          <button
            onClick={() => {
              setDebugMode(!debugMode);
              fetchStudentMatches(!debugMode);
            }}
            className={`col-span-2 sm:col-span-1 px-3 sm:px-4 py-2 rounded-lg transition-colors text-sm sm:text-base ${
              debugMode 
                ? 'bg-orange-600 text-white hover:bg-orange-700' 
                : 'bg-gray-400 text-white hover:bg-gray-500'
            }`}
          >
            {debugMode ? 'Hide Debug' : 'Show Debug'}
          </button>
        </div>

        {/* Debug Information */}
        {debugMode && debugInfo && (
          <div className="mb-6 bg-yellow-50 dark:bg-yellow-900 border border-yellow-200 dark:border-yellow-700 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-yellow-800 dark:text-yellow-200 mb-3">
              Debug Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
              <div>
                <p className="font-medium text-yellow-700 dark:text-yellow-300">
                  Total Students: {debugInfo.total_students}
                </p>
              </div>
              <div>
                <p className="font-medium text-yellow-700 dark:text-yellow-300">
                  Total Recruiters: {debugInfo.total_recruiters}
                </p>
              </div>
              <div>
                <p className="font-medium text-yellow-700 dark:text-yellow-300">
                  Students Processed: {debugInfo.students_processed}
                </p>
              </div>
              <div>
                <p className="font-medium text-yellow-700 dark:text-yellow-300">
                  Students with Matches: {debugInfo.students_with_matches}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Student List */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
          <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
              <h2 className="text-base sm:text-xl font-semibold text-gray-900 dark:text-white">
                Students with Organization Matches
              </h2>
              <span className="text-sm text-gray-600 dark:text-gray-400 font-medium bg-gray-100 dark:bg-gray-700 px-3 py-1 rounded-full w-fit">
                {filteredStudents.length} total
              </span>
            </div>
          </div>

          {filteredStudents.length > 0 ? (
            <ResponsiveTable 
              data={filteredStudents}
              renderMobileCard={(studentData, index) => (
                <MobileAdminStudentMatchCard
                  key={`${studentData.student_info.id}-${index}`}
                  studentData={studentData}
                  isSelected={selectedStudents.has(studentData.student_info.id)}
                  onToggleSelect={() => toggleStudentSelection(studentData.student_info.id)}
                  onGenerateToken={generateIndividualToken}
                  tokenGenerating={tokenGenerating}
                  isExpanded={expandedMobileCards.has(index)}
                  onToggleExpand={() => toggleMobileCard(index)}
                  expandedOrgItems={expandedOrgItems}
                  setExpandedOrgItems={setExpandedOrgItems}
                />
              )}
            >
              <div className="space-y-0">
                {filteredStudents.map((studentData, index) => {
                  const isExpanded = expandedStudents.has(index);
                  const { student_info, matching_organizations, match_count } = studentData;
                  const isSelected = selectedStudents.has(student_info.id);
                  
                  return (
                    <div key={index} className="border-b border-gray-200 dark:border-gray-700 last:border-b-0">
                      {/* Student Header */}
                      <div 
                        className="p-6 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                        onClick={() => toggleStudent(index)}
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex items-start gap-3 flex-1">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleStudentSelection(student_info.id)}
                              onClick={(e) => e.stopPropagation()} // Prevent card expansion
                              className="mt-1 w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                            />
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-3">
                                <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                                  {student_info.name}
                                </h3>
                                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                                  match_count > 0 
                                    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                    : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                                }`}>
                                  {match_count} {match_count === 1 ? 'Organization Match' : 'Organization Matches'}
                                </span>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    generateIndividualToken(student_info.id);
                                  }}
                                  disabled={tokenGenerating}
                                  className="px-2 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                                >
                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m0 0a2 2 0 012 2m-2-2v6m0 0a2 2 0 01-2 2m2-2a2 2 0 002-2m-4 4H9.5a2.5 2.5 0 010-5H11m0 0a2 2 0 012-2m-2 2v4a2 2 0 01-2 2m2-2a2 2 0 002-2z" />
                                  </svg>
                                  Token
                                </button>
                              </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600 dark:text-gray-300">
                              <div>
                                <p><strong>Email:</strong> {student_info.email}</p>
                                <p><strong>University:</strong> {student_info.university}</p>
                              </div>
                              <div>
                                <p><strong>GPA:</strong> <span className="text-green-600 dark:text-green-400 font-medium">{student_info.gpa}</span></p>
                                <p><strong>Major:</strong> {student_info.major}</p>
                                <p><strong>Class Year:</strong> {student_info.class_year}</p>
                              </div>
                            </div>
                            </div>
                          </div>
                          
                          <div className="ml-4">
                            <svg 
                              className={`w-6 h-6 text-gray-400 transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                              fill="none" 
                              stroke="currentColor" 
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </div>
                        </div>
                      </div>

                      {/* Expanded Content */}
                      {isExpanded && (
                        <div className="border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-750">
                          {/* Matching Organizations */}
                          <div className="p-6">
                            <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                              Matching Organizations ({match_count})
                            </h4>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                              {matching_organizations.map((org, orgIndex) => (
                                <div key={orgIndex} className="bg-white dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600 shadow-sm">
                                  <div className="flex items-center gap-2 mb-2">
                                    <h5 className="font-semibold text-gray-900 dark:text-white text-sm">
                                      {org.organization}
                                    </h5>
                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                      org.approved
                                        ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                                        : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                                    }`}>
                                      {org.approved ? 'APPROVED' : 'PENDING'}
                                    </span>
                                  </div>
                                  
                                  {/* Basic Info */}
                                  <div className="text-xs text-gray-600 dark:text-gray-300 mb-3 space-y-1">
                                    <p><strong>Recruiter:</strong> {org.recruiter_name}</p>
                                    <p><strong>Email:</strong> {org.recruiter_email}</p>
                                    <p><strong>Min GPA:</strong> {org.minimum_gpa}</p>
                                  </div>
                                  
                                  {/* Requirements */}
                                  <div className="space-y-2 border-t border-gray-200 dark:border-gray-600 pt-2">
                                    <OrgRequirementDropdown 
                                      label="Eligible Majors"
                                      items={org.eligible_majors}
                                      orgIndex={`${index}-${orgIndex}`}
                                      fieldName="majors"
                                      expandedItems={expandedOrgItems}
                                      setExpandedItems={setExpandedOrgItems}
                                    />
                                    <OrgRequirementDropdown 
                                      label="Preferred Institutions"
                                      items={org.preferred_institutions}
                                      orgIndex={`${index}-${orgIndex}`}
                                      fieldName="institutions"
                                      expandedItems={expandedOrgItems}
                                      setExpandedItems={setExpandedOrgItems}
                                    />
                                    <div>
                                      <p className="font-medium text-gray-700 dark:text-gray-300 text-xs">Preferred Class Years:</p>
                                      <p className="text-gray-600 dark:text-gray-400 text-xs">
                                        {org.preferred_class_years ? org.preferred_class_years.join(', ') : 'Not specified'}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </ResponsiveTable>
          ) : (
            <div className="p-8 text-center">
              <div className="text-gray-400 dark:text-gray-500 text-4xl sm:text-6xl mb-4">🎓</div>
              <p className="text-gray-600 dark:text-gray-400 text-base sm:text-lg font-medium">
                {searchTerm || universityFilter 
                  ? 'No students found matching your search criteria'
                  : 'No students found with organization matches'
                }
              </p>
              <p className="text-gray-500 dark:text-gray-500 text-sm mt-2">
                {searchTerm || universityFilter 
                  ? 'Try adjusting your search term or university filter.'
                  : 'No students currently match the requirements of any organizations.'
                }
              </p>
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default AdminStudentMatches;